import type { Effect, Cost, Card } from '../types';
import { GameState } from './GameState';
import { Deck } from './Deck';

export interface EffectResult {
  cardAction?: {
    exhaust?: boolean;
    createOpportunity?: boolean;
    unlockInspiration?: boolean;
    thinkingModal?: boolean;
  };
  discardedCards?: Card[];
}

export function processEffects(
  effects: Effect[] | undefined,
  gameState: GameState,
  _deck: Deck,
  _currentCard: { name: string; description: string }
): EffectResult {
  const result: EffectResult = {};
  
  if (!effects || !Array.isArray(effects)) {
    return result;
  }
  
  for (const effect of effects) {
    if (!effect || typeof effect !== 'object') {
      continue;
    }
    // Resource effects
    if (effect.money !== undefined) {
      gameState.modifyResource('money', effect.money);
    }
    if (effect.food !== undefined) {
      gameState.modifyResource('food', effect.food);
    }
    if (effect.motivation !== undefined) {
      gameState.modifyResource('motivation', effect.motivation);
    }
    if (effect.memory !== undefined) {
      gameState.modifyResource('memory', effect.memory);
    }
    if (effect.connections !== undefined) {
      gameState.modifyResource('connections', effect.connections);
    }
    if (effect.health !== undefined) {
      gameState.modifyResource('health', effect.health);
    }
    if (effect.bad_habit !== undefined) {
      gameState.modifyResource('bad_habit', effect.bad_habit);
    }
    if (effect.sanity !== undefined) {
      gameState.modifyResource('sanity', effect.sanity);
    }
    if (effect.dignity !== undefined) {
      gameState.modifyResource('dignity', effect.dignity);
    }
    
    // Stat effects - level_up is handled separately via modal
    // We don't process level_up here, it's handled in the main game loop
    if (effect.level_up_tenacity !== undefined) {
      gameState.modifyStat('tenacity', effect.level_up_tenacity);
    }
    if (effect.level_up_smarts !== undefined) {
      gameState.modifyStat('smarts', effect.level_up_smarts);
    }
    if (effect.level_up_social !== undefined) {
      gameState.modifyStat('social', effect.level_up_social);
    }
    
    // Card action effects
    if (effect.exhaust_this_event) {
      result.cardAction = result.cardAction || {};
      result.cardAction.exhaust = true;
    }
    if (effect.create_opportunity) {
      result.cardAction = result.cardAction || {};
      result.cardAction.createOpportunity = true;
      // TODO: Implement create_opportunity logic later
    }
    if (effect.unlock_inspiration) {
      result.cardAction = result.cardAction || {};
      result.cardAction.unlockInspiration = true;
      // TODO: Implement unlock_inspiration logic later
    }
    if (effect.thinking_modal) {
      result.cardAction = result.cardAction || {};
      result.cardAction.thinkingModal = true;
      // TODO: Implement thinking_modal logic later
    }
  }
  
  return result;
}

export function applyCost(
  cost: Cost,
  gameState: GameState,
  deck: Deck
): { success: boolean; usedSanity: boolean; sanityCost: number; discardedCards?: Card[] } {
  let usedSanity = false;
  let sanityCost = 0;
  
  // Check time cost first (must have enough cards)
  // Note: Current card is already removed from deck by playCard(), so use deck size directly
  if (cost.time !== undefined && cost.time > 0) {
    const availableToDiscard = deck.getDeckSize();
    if (availableToDiscard < cost.time) {
      return { success: false, usedSanity: false, sanityCost: 0 };
    }
  }
  
  // Apply regular resource costs (must have enough - no going negative)
  // These must be fully paid - no Sanity fallback
  if (cost.money !== undefined && cost.money > 0) {
    const current = gameState.getResource('money');
    if (current < cost.money) {
      return { success: false, usedSanity: false, sanityCost: 0, discardedCards: [] };
    }
    gameState.modifyResource('money', -cost.money);
  }
  if (cost.food !== undefined && cost.food > 0) {
    const current = gameState.getResource('food');
    if (current < cost.food) {
      return { success: false, usedSanity: false, sanityCost: 0, discardedCards: [] };
    }
    gameState.modifyResource('food', -cost.food);
  }
  if (cost.motivation !== undefined && cost.motivation > 0) {
    const current = gameState.getResource('motivation');
    if (current < cost.motivation) {
      return { success: false, usedSanity: false, sanityCost: 0, discardedCards: [] };
    }
    gameState.modifyResource('motivation', -cost.motivation);
  }
  if (cost.memory !== undefined && cost.memory > 0) {
    const current = gameState.getResource('memory');
    if (current < cost.memory) {
      return { success: false, usedSanity: false, sanityCost: 0, discardedCards: [] };
    }
    gameState.modifyResource('memory', -cost.memory);
  }
  if (cost.connections !== undefined && cost.connections > 0) {
    const current = gameState.getResource('connections');
    if (current < cost.connections) {
      return { success: false, usedSanity: false, sanityCost: 0, discardedCards: [] };
    }
    gameState.modifyResource('connections', -cost.connections);
  }
  
  // Apply health cost (always payable - can go negative, then resets to 1 and Sanity drops)
  if (cost.health !== undefined && cost.health > 0) {
    gameState.modifyResource('health', -cost.health);
  }
  
  // Apply sanity cost (always payable - can go to 0 which is defeat)
  if (cost.sanity !== undefined && cost.sanity > 0) {
    gameState.modifyResource('sanity', -cost.sanity);
  }
  
  // Apply time cost - return discarded cards for notifications
  let discardedCards: Card[] = [];
  if (cost.time !== undefined && cost.time > 0) {
    discardedCards = deck.discardFromTop(cost.time);
  }
  
  return { success: true, usedSanity, sanityCost, discardedCards };
}

